import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';

export default function TicketForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [airlines, setAirlines] = useState([]);

  const [form, setForm] = useState({
    ticket_code: '',
    ticket_date: dayjs().format('YYYY-MM-DD'),
    client_id: '',
    client_name: '',
    supplier_id: '',
    destination_id: '',
    airline_id: '',
    passenger_name: '',
    passenger_phone: '',
    departure_date: '',
    return_date: '',
    fare: '',
    tax: '',
    notes: '',
  });

  useEffect(() => {
    fetchDropdowns();
    if (id) fetchTicket();
  }, [id]);

  const fetchDropdowns = async () => {
    try {
      const [c, s, d, a] = await Promise.all([
        api.get('/clients'),
        api.get('/suppliers'),
        api.get('/destinations'),
        api.get('/airlines'),
      ]);
      setClients(c.data);
      setSuppliers(s.data);
      setDestinations(d.data);
      setAirlines(a.data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchTicket = async () => {
    try {
      const response = await api.get(`/tickets/${id}`);
      const t = response.data;
      setForm({
        ticket_code: t.ticket_code,
        ticket_date: dayjs(t.ticket_date).format('YYYY-MM-DD'),
        client_id: t.client_id || '',
        client_name: t.client_name || '',
        supplier_id: t.supplier_id || '',
        destination_id: t.destination_id || '',
        airline_id: t.airline_id || '',
        passenger_name: t.passenger_name,
        passenger_phone: t.passenger_phone || '',
        departure_date: t.departure_date ? dayjs(t.departure_date).format('YYYY-MM-DD') : '',
        return_date: t.return_date ? dayjs(t.return_date).format('YYYY-MM-DD') : '',
        fare: t.fare,
        tax: t.tax,
        notes: t.notes || '',
      });
    } catch (error) {
      toast.error('Gabim');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (id) {
        await api.put(`/tickets/${id}`, form);
        toast.success('Bileta u përditësua!');
      } else {
        await api.post('/tickets', form);
        toast.success('Bileta u shtua!');
      }
      navigate('/tickets');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gabim');
    } finally {
      setLoading(false);
    }
  };

  const handleClientChange = (clientId) => {
    const client = clients.find(c => c.id === parseInt(clientId));
    setForm({ ...form, client_id: clientId, client_name: client?.name || '' });
  };

  const total = (parseFloat(form.fare) || 0) + (parseFloat(form.tax) || 0);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">{id ? 'Ndrysho Biletë' : 'Shto Biletë të Re'}</h1>

      <form onSubmit={handleSubmit} className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Kodi Biletës *</label>
            <input type="text" value={form.ticket_code} onChange={(e) => setForm({ ...form, ticket_code: e.target.value })} className="input-field" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data Biletës *</label>
            <input type="date" value={form.ticket_date} onChange={(e) => setForm({ ...form, ticket_date: e.target.value })} className="input-field" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Klienti</label>
            <select value={form.client_id} onChange={(e) => handleClientChange(e.target.value)} className="input-field">
              <option value="">-- Zgjidh --</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Furnitori</label>
            <select value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })} className="input-field">
              <option value="">-- Zgjidh --</option>
              {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Destinacioni</label>
            <select value={form.destination_id} onChange={(e) => setForm({ ...form, destination_id: e.target.value })} className="input-field">
              <option value="">-- Zgjidh --</option>
              {destinations.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Kompania Ajrore</label>
            <select value={form.airline_id} onChange={(e) => setForm({ ...form, airline_id: e.target.value })} className="input-field">
              <option value="">-- Zgjidh --</option>
              {airlines.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Emri Pasagjerit *</label>
            <input type="text" value={form.passenger_name} onChange={(e) => setForm({ ...form, passenger_name: e.target.value })} className="input-field" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tel Pasagjerit</label>
            <input type="text" value={form.passenger_phone} onChange={(e) => setForm({ ...form, passenger_phone: e.target.value })} className="input-field" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data Nisjes</label>
            <input type="date" value={form.departure_date} onChange={(e) => setForm({ ...form, departure_date: e.target.value })} className="input-field" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data Kthimit</label>
            <input type="date" value={form.return_date} onChange={(e) => setForm({ ...form, return_date: e.target.value })} className="input-field" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tarifa (€) *</label>
            <input type="number" step="0.01" value={form.fare} onChange={(e) => setForm({ ...form, fare: e.target.value })} className="input-field" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">TSC/Fitim (€) *</label>
            <input type="number" step="0.01" value={form.tax} onChange={(e) => setForm({ ...form, tax: e.target.value })} className="input-field" required />
          </div>
          <div className="md:col-span-2 lg:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Shënime</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-field" rows="2" />
          </div>
        </div>

        <div className="mt-6 pt-4 border-t flex items-center justify-between">
          <div className="text-xl font-bold text-gray-900">Total: €{total.toFixed(2)}</div>
          <div className="flex gap-3">
            <button type="button" onClick={() => navigate('/tickets')} className="btn-secondary">Anulo</button>
            <button type="submit" disabled={loading} className="btn-primary disabled:opacity-50">
              {loading ? 'Duke ruajtur...' : 'Ruaj Biletën'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
